import React from 'react';
import { IEvent } from '@interfaces/event';
import { IUser } from '@interfaces/user';
import { IConfigs } from '@interfaces/profile';
import { IParticipant } from '@interfaces/participants';

import {
  IActivePrivateChannel,
  IPrivateChannel,
  IStreamChannel,
  IStreamChat,
  ISetStreamChat,
  ISetStreamChannel,
  ISetRoomStreamChannel,
} from '@core/chat/StreamChat/interfaces';

export interface IChatState {
  open: boolean;
  streamClient: IStreamChat | null;
  eventStreamChannel: IStreamChannel | null;
  eventRoomStreamChannel: IStreamChannel | null;
  privateChannels: IPrivateChannel[];
  isUnreadEventChannel: boolean;
  isUnreadPrivateChannel: boolean;
  isUnreadEventRoomChannel: boolean;
  loadingPrivateChannel: string | null;
  resolvedFailedMessageIds: string[];
  initialLoading: boolean;
  activeTab: ChatTabs;
  activePrivateChannel: IActivePrivateChannel | null;
  setOpen: (open: boolean) => void;
  setStreamClient: (streamClient: IStreamChat | null) => void;
  setEventStreamChannel: (eventStreamChannel: IStreamChannel | null) => void;
  setEventRoomStreamChannel: (
    eventRoomStreamChannel: IStreamChannel | null,
  ) => void;
  setPrivateChannels: (privateChannels: IPrivateChannel[]) => void;
  setLoadingPrivateChannel: (participantUserId: string | null) => void;
  addPrivateChannel: (privateChannel: IPrivateChannel) => void;
  setIsUnreadEventChannel: (isUnread: boolean) => void;
  setIsUnreadEventRoomChannel: (isUnread: boolean) => void;
  setIsUnreadPrivateChannel: (isUnread: boolean) => void;
  setInitialLoading: (initialLoading: boolean) => void;
  setActiveTab: (activeTab: ChatTabs) => void;
  setActivePrivateChannel: (privateChannel: IPrivateChannel | null) => void;
  setOpenDeleteChatModal: (open: boolean) => void;
  openDeleteChatModal: boolean;
  resetState: () => void;
  resetChangeEventChatState: () => void;
  setResolvedFailedMessageIds: (resolvedFailedMessageIds: string[]) => void;
  chatDrawerWidth: number;
  setChatDrawerWidth: (chatWidth: number) => void;
}

export enum ChatTabs {
  Global = 'global',
  Private = 'private',
  Broadcast = 'broadcast',
}

export enum ThirdPartyChatProvider {
  StreamChat = 'STREAM_CHAT',
}

export interface IUseChatHandlers {
  currentEvent: IEvent | undefined;
  streamChatToken: string;
}

export interface IUseInitializeStreamClientConnection {
  streamChatToken: string;
  streamClient: IStreamChat | null;
  setStreamClient: ISetStreamChat;
  user: IUser;
  streamChatMemberId: string;
  configs: IConfigs | null;
}

export interface IUseSetInitialChannels {
  streamClient: IStreamChat | null;
  setEventStreamChannel: ISetStreamChannel;
  setEventRoomStreamChannel: ISetRoomStreamChannel;
  setPrivateChannels: (privateChannels: IPrivateChannel[]) => void;
  currentEventId: string;
  userId: string;
  currentRoomId?: string;
}

export interface IUseCheckInitialUnreadChannels {
  eventChannel: IStreamChannel | null;
  eventRoomChannel: IStreamChannel | null;
  privateChannels: IPrivateChannel[];
  setInitialLoading: (initialLoading: boolean) => void;
  setIsUnreadEventChannel: (isUnread: boolean) => void;
  setIsUnreadEventRoomChannel: (isUnread: boolean) => void;
  setIsUnreadPrivateChannel: (isUnread: boolean) => void;
}

export interface IUseCheckUnreadChannel {
  privateChannels: IPrivateChannel[];
  setIsUnreadEventChannel: (isUnread: boolean) => void;
  setIsUnreadEventRoomChannel: (isUnread: boolean) => void;
  setIsUnreadPrivateChannel: (isUnread: boolean) => void;
  streamClient: IStreamChat | null;
  currentEventId: string;
  userId: string;
  isOpenChat: boolean;
  participants: IParticipant[];
}

export interface IUseNewChannel {
  streamClient: IStreamChat | null;
  hasEventStreamChannel: boolean;
  setEventStreamChannel: ISetStreamChannel;
  addPrivateChannel: (privateChannels: IPrivateChannel) => void;
  currentEventId: string;
  userId: string;
}

export type CheckIsGlobalTabType = (tab: ChatTabs) => boolean;

export type CheckIsBroadcastTabType = (tab: ChatTabs) => boolean;

export interface ILibraryImplementation {
  videoRun: boolean;
  setVideoRun: React.Dispatch<React.SetStateAction<boolean>>;
  getVideoStatus: () => void;
}

export type UseInitChatProviderType = (props: IUseChatHandlers) => void;
